import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { rentalService } from '../../services/rentalService';
import type {
  RentalContract,
  RentalMachine,
  MachineCondition,
  MachineReturnItem,
  SelectiveReturnData,
  RentalContractStatus,
  AvailableMachineForReplacement
} from '../../types/rental';

interface SelectiveReturnModalProps {
  isOpen: boolean;
  onClose: () => void;
  contract: RentalContract;
}

interface MachineReturnState {
  machineId: string;
  selected: boolean;
  returnCondition: MachineCondition;
  notes: string;
  wantsReplacement: boolean;
  replacement?: {
    serialNumberId: string;
    monthlyRentalFee?: number;
    initialBottlesQty?: number;
    initialBottlesMl?: number;
    bottlesProductId?: string;
  };
}

// ── style tokens ─────────────────────────────────────────────────────────────
const inputStyle: React.CSSProperties = {
  background: '#f4efe2',
  border: '1px solid rgba(92,78,51,0.25)',
  borderRadius: 4,
  padding: '9px 12px',
  fontFamily: "'Inter', sans-serif",
  fontSize: 12,
  color: '#5b5a52',
  outline: 'none',
  width: '100%',
};

const labelStyle: React.CSSProperties = {
  display: 'block',
  fontFamily: "'Inter', sans-serif",
  fontSize: 9,
  letterSpacing: '0.2em',
  textTransform: 'uppercase',
  color: '#6b6a5c',
  marginBottom: 6,
};

const thStyle: React.CSSProperties = {
  padding: '10px 14px',
  textAlign: 'left',
  fontFamily: "'Inter', sans-serif",
  fontSize: 9,
  letterSpacing: '0.15em',
  textTransform: 'uppercase',
  color: '#6b6a5c',
  borderBottom: '1px solid rgba(92,78,51,0.15)',
  background: '#faf7ee',
  whiteSpace: 'nowrap',
};

const tdStyle: React.CSSProperties = {
  padding: '12px 14px',
  borderBottom: '1px solid rgba(239,232,213,0.6)',
  fontFamily: "'Inter', sans-serif",
  fontSize: 12,
  color: '#5b5a52',
  verticalAlign: 'top',
};

const primaryBtn: React.CSSProperties = {
  background: 'linear-gradient(135deg, #d97706, #b45309)',
  color: '#1c2033',
  border: 'none',
  borderRadius: 4,
  padding: '9px 22px',
  fontFamily: "'Inter', sans-serif",
  fontSize: 10,
  letterSpacing: '0.15em',
  textTransform: 'uppercase',
  cursor: 'pointer',
  fontWeight: 600,
};

const ghostBtn: React.CSSProperties = {
  background: 'transparent',
  border: '1px solid rgba(92,78,51,0.25)',
  borderRadius: 4,
  padding: '9px 22px',
  fontFamily: "'Inter', sans-serif",
  fontSize: 10,
  letterSpacing: '0.15em',
  textTransform: 'uppercase',
  color: '#6b6a5c',
  cursor: 'pointer',
};

const miniInput: React.CSSProperties = {
  background: '#f4efe2',
  border: '1px solid rgba(92,78,51,0.25)',
  borderRadius: 4,
  padding: '5px 8px',
  fontFamily: "'Inter', sans-serif",
  fontSize: 11,
  color: '#5b5a52',
  outline: 'none',
  width: '100%',
};

export const SelectiveReturnModal: React.FC<SelectiveReturnModalProps> = ({
  isOpen,
  onClose,
  contract
}) => {
  const queryClient = useQueryClient();

  const [returnDate, setReturnDate] = useState(new Date().toISOString().split('T')[0]);
  const [setContractStatus, setSetContractStatus] = useState<RentalContractStatus | ''>('');
  const [machineStates, setMachineStates] = useState<Record<string, MachineReturnState>>({});
  const [lookupProductId, setLookupProductId] = useState<string | null>(null);

  useEffect(() => {
    if (contract?.machines) {
      const rentedMachines = contract.machines.filter(m => m.status === 'RENTED');
      const initialStates: Record<string, MachineReturnState> = {};
      rentedMachines.forEach(machine => {
        initialStates[machine.id] = {
          machineId: machine.id,
          selected: false,
          returnCondition: 'GOOD' as MachineCondition,
          notes: '',
          wantsReplacement: false
        };
      });
      setMachineStates(initialStates);
    }
  }, [contract]);

  const { data: availableMachinesData } = useQuery({
    queryKey: ['available-machines', lookupProductId],
    queryFn: () => rentalService.getAvailableMachinesByProduct(lookupProductId!),
    enabled: !!lookupProductId
  });

  const returnMutation = useMutation({
    mutationFn: (data: SelectiveReturnData) =>
      rentalService.selectiveReturnAndReplace(contract.id, data),
    onSuccess: (response) => {
      queryClient.invalidateQueries({ queryKey: ['rental-contracts'] });
      queryClient.invalidateQueries({ queryKey: ['rental-contract', contract.id] });
      queryClient.invalidateQueries({ queryKey: ['rental-dashboard'] });
      alert(response.message || 'Machines processed successfully!');
      onClose();
    },
    onError: (error: any) => {
      alert(error.response?.data?.message || 'Failed to process machine returns');
    }
  });

  const rentedMachines = contract?.machines?.filter(m => m.status === 'RENTED') || [];
  const selectedMachines = Object.values(machineStates).filter(s => s.selected);
  const allSelectedReturned = selectedMachines.length === rentedMachines.length;
  const hasReplacements = selectedMachines.some(s => s.wantsReplacement);

  const handleMachineToggle = (machineId: string) => {
    setMachineStates(prev => ({
      ...prev,
      [machineId]: { ...prev[machineId], selected: !prev[machineId].selected }
    }));
  };

  const handleConditionChange = (machineId: string, condition: MachineCondition) => {
    setMachineStates(prev => ({
      ...prev,
      [machineId]: { ...prev[machineId], returnCondition: condition }
    }));
  };

  const handleNotesChange = (machineId: string, notes: string) => {
    setMachineStates(prev => ({
      ...prev,
      [machineId]: { ...prev[machineId], notes }
    }));
  };

  const handleReplacementToggle = (machineId: string, machine: RentalMachine) => {
    const currentState = machineStates[machineId];
    const newWantsReplacement = !currentState.wantsReplacement;
    setMachineStates(prev => ({
      ...prev,
      [machineId]: {
        ...prev[machineId],
        wantsReplacement: newWantsReplacement,
        replacement: newWantsReplacement ? {
          serialNumberId: '',
          monthlyRentalFee: machine.monthlyRentalFee,
          initialBottlesQty: undefined,
          initialBottlesMl: undefined,
          bottlesProductId: undefined
        } : undefined
      }
    }));
    if (newWantsReplacement && machine.serialNumber?.productId) {
      setLookupProductId(machine.serialNumber.productId);
    }
  };

  const handleReplacementSerialChange = (machineId: string, serialNumberId: string) => {
    setMachineStates(prev => ({
      ...prev,
      [machineId]: {
        ...prev[machineId],
        replacement: { ...prev[machineId].replacement!, serialNumberId }
      }
    }));
  };

  const handleReplacementFieldChange = (
    machineId: string,
    field: 'monthlyRentalFee' | 'initialBottlesQty' | 'initialBottlesMl' | 'bottlesProductId',
    value: any
  ) => {
    setMachineStates(prev => ({
      ...prev,
      [machineId]: {
        ...prev[machineId],
        replacement: { ...prev[machineId].replacement!, [field]: value }
      }
    }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const selectedStates = Object.values(machineStates).filter(s => s.selected);
    if (selectedStates.length === 0) {
      alert('Please select at least one machine to return');
      return;
    }
    for (const state of selectedStates) {
      if (state.wantsReplacement && !state.replacement?.serialNumberId) {
        alert('Please select a replacement serial number for all machines marked for replacement');
        return;
      }
    }
    const returns: MachineReturnItem[] = selectedStates.map(state => ({
      machineId: state.machineId,
      returnCondition: state.returnCondition,
      notes: state.notes || undefined,
      replacement: state.wantsReplacement ? state.replacement : undefined
    }));
    const data: SelectiveReturnData = {
      returns,
      returnDate,
      setContractStatus: setContractStatus || undefined
    };
    returnMutation.mutate(data);
  };

  const getMachineProduct = (machine: RentalMachine) => machine.serialNumber?.product;

  const getAvailableMachinesForProduct = (productId: string) => {
    if (lookupProductId === productId) return availableMachinesData?.data || [];
    return [];
  };

  if (!isOpen) return null;

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(7,7,15,0.85)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
      <div style={{ background: '#ffffff', border: '1px solid rgba(92,78,51,0.25)', borderRadius: 8, width: '100%', maxWidth: 1100, maxHeight: '90vh', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>

        {/* Header */}
        <div style={{ padding: '24px 32px', borderBottom: '1px solid rgba(92,78,51,0.15)', display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
          <div>
            <h2 style={{ fontFamily: "'Inter', sans-serif", fontSize: 24, fontWeight: 600, color: '#1c2033', margin: 0 }}>
              Manage Machine Returns
            </h2>
            <p style={{ fontFamily: "'Inter', sans-serif", fontSize: 12, color: '#6b6a5c', margin: '6px 0 0' }}>
              Contract: {contract.contractNumber} — {contract.customer?.name}
            </p>
          </div>
          <button
            onClick={onClose}
            style={{ background: 'none', border: 'none', color: '#6b6a5c', cursor: 'pointer', padding: 4, marginTop: 4 }}
          >
            <svg width="20" height="20" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Body */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '24px 32px' }}>
          <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>

            {/* Global Settings */}
            <div style={{ background: '#faf7ee', border: '1px solid rgba(92,78,51,0.15)', borderRadius: 6, padding: 20 }}>
              <h3 style={{ fontFamily: "'Inter', sans-serif", fontSize: 18, fontWeight: 600, color: '#1c2033', margin: '0 0 16px' }}>
                Return Settings
              </h3>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                <div>
                  <label style={labelStyle}>Return Date <span style={{ color: '#c0504d' }}>*</span></label>
                  <input
                    type="date"
                    value={returnDate}
                    onChange={(e) => setReturnDate(e.target.value)}
                    style={inputStyle}
                    required
                  />
                </div>
                <div>
                  <label style={labelStyle}>Update Contract Status (Optional)</label>
                  <select
                    value={setContractStatus}
                    onChange={(e) => setSetContractStatus(e.target.value as RentalContractStatus | '')}
                    style={inputStyle}
                  >
                    <option value="">Keep Current Status</option>
                    <option value="INACTIVE">INACTIVE</option>
                    <option value="TERMINATED">TERMINATED</option>
                  </select>
                  <p style={{ fontFamily: "'Inter', sans-serif", fontSize: 11, color: '#6b6a5c', marginTop: 4 }}>
                    {allSelectedReturned && !hasReplacements
                      ? 'All machines will be returned. You may want to update the contract status.'
                      : 'Contract will remain ACTIVE if machines are replaced or not all returned'}
                  </p>
                </div>
              </div>
            </div>

            {/* Machine Selection Table */}
            <div>
              <h3 style={{ fontFamily: "'Inter', sans-serif", fontSize: 18, fontWeight: 600, color: '#1c2033', margin: '0 0 16px' }}>
                Select Machines to Return ({selectedMachines.length} of {rentedMachines.length} selected)
              </h3>

              {rentedMachines.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '40px 0', color: '#6b6a5c', fontFamily: "'Inter', sans-serif", fontSize: 13 }}>
                  No rented machines available to return
                </div>
              ) : (
                <div style={{ overflowX: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead>
                      <tr>
                        <th style={thStyle}>Select</th>
                        <th style={thStyle}>Machine</th>
                        <th style={thStyle}>Product</th>
                        <th style={thStyle}>Condition</th>
                        <th style={thStyle}>Notes</th>
                        <th style={thStyle}>Replacement</th>
                      </tr>
                    </thead>
                    <tbody>
                      {rentedMachines.map((machine) => {
                        const state = machineStates[machine.id];
                        const product = getMachineProduct(machine);
                        const availableMachines = product ? getAvailableMachinesForProduct(product.id) : [];

                        return (
                          <tr
                            key={machine.id}
                            style={{ background: state?.selected ? 'rgba(241,245,249,1)' : 'transparent' }}
                          >
                            {/* Checkbox */}
                            <td style={tdStyle}>
                              <input
                                type="checkbox"
                                checked={state?.selected || false}
                                onChange={() => handleMachineToggle(machine.id)}
                                style={{ width: 16, height: 16, accentColor: '#d97706', cursor: 'pointer' }}
                              />
                            </td>

                            {/* Machine Info */}
                            <td style={tdStyle}>
                              <div style={{ color: '#1c2033', fontWeight: 500 }}>{machine.serialNumber?.serialNumber}</div>
                              <div style={{ fontSize: 11, color: '#6b6a5c', marginTop: 2 }}>
                                Monthly Fee: ${machine.monthlyRentalFee}
                              </div>
                            </td>

                            {/* Product */}
                            <td style={tdStyle}>
                              <div style={{ color: '#5b5a52' }}>{product?.name}</div>
                              <div style={{ fontSize: 11, color: '#6b6a5c', marginTop: 2 }}>{product?.sku}</div>
                            </td>

                            {/* Condition */}
                            <td style={tdStyle}>
                              <select
                                value={state?.returnCondition || 'GOOD'}
                                onChange={(e) => handleConditionChange(machine.id, e.target.value as MachineCondition)}
                                disabled={!state?.selected}
                                style={{ ...miniInput, opacity: !state?.selected ? 0.4 : 1, cursor: !state?.selected ? 'not-allowed' : 'pointer' }}
                              >
                                <option value="EXCELLENT">EXCELLENT</option>
                                <option value="GOOD">GOOD</option>
                                <option value="FAIR">FAIR</option>
                                <option value="POOR">POOR</option>
                                <option value="DAMAGED">DAMAGED</option>
                              </select>
                            </td>

                            {/* Notes */}
                            <td style={tdStyle}>
                              <input
                                type="text"
                                value={state?.notes || ''}
                                onChange={(e) => handleNotesChange(machine.id, e.target.value)}
                                disabled={!state?.selected}
                                placeholder="Return notes..."
                                style={{ ...miniInput, opacity: !state?.selected ? 0.4 : 1, cursor: !state?.selected ? 'not-allowed' : 'text' }}
                              />
                            </td>

                            {/* Replacement */}
                            <td style={tdStyle}>
                              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                                <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: !state?.selected ? 'not-allowed' : 'pointer' }}>
                                  <input
                                    type="checkbox"
                                    checked={state?.wantsReplacement || false}
                                    onChange={() => handleReplacementToggle(machine.id, machine)}
                                    disabled={!state?.selected}
                                    style={{ width: 14, height: 14, accentColor: '#d97706', cursor: !state?.selected ? 'not-allowed' : 'pointer', opacity: !state?.selected ? 0.4 : 1 }}
                                  />
                                  <span style={{ fontFamily: "'Inter', sans-serif", fontSize: 9, letterSpacing: '0.1em', textTransform: 'uppercase', color: '#6b6a5c' }}>Replace</span>
                                </label>

                                {state?.wantsReplacement && (
                                  <div style={{ display: 'flex', flexDirection: 'column', gap: 6, paddingLeft: 22 }}>
                                    <select
                                      value={state.replacement?.serialNumberId || ''}
                                      onChange={(e) => handleReplacementSerialChange(machine.id, e.target.value)}
                                      style={miniInput}
                                      required={state.wantsReplacement}
                                    >
                                      <option value="">Select Replacement</option>
                                      {availableMachines.map((avail: AvailableMachineForReplacement) => (
                                        <option key={avail.id} value={avail.id}>
                                          {avail.serialNumber} - {avail.warehouseName || 'No Warehouse'}
                                        </option>
                                      ))}
                                    </select>
                                    <input
                                      type="number"
                                      value={state.replacement?.monthlyRentalFee || ''}
                                      onChange={(e) => handleReplacementFieldChange(machine.id, 'monthlyRentalFee', parseFloat(e.target.value))}
                                      placeholder="Monthly Fee"
                                      style={miniInput}
                                    />
                                    <div style={{ display: 'flex', gap: 6 }}>
                                      <input
                                        type="number"
                                        value={state.replacement?.initialBottlesQty || ''}
                                        onChange={(e) => handleReplacementFieldChange(machine.id, 'initialBottlesQty', parseInt(e.target.value))}
                                        placeholder="Bottles Qty"
                                        style={{ ...miniInput, width: '50%' }}
                                      />
                                      <input
                                        type="number"
                                        value={state.replacement?.initialBottlesMl || ''}
                                        onChange={(e) => handleReplacementFieldChange(machine.id, 'initialBottlesMl', parseInt(e.target.value))}
                                        placeholder="Bottles ML"
                                        style={{ ...miniInput, width: '50%' }}
                                      />
                                    </div>
                                  </div>
                                )}
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            {/* Form Actions */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingTop: 16, borderTop: '1px solid rgba(92,78,51,0.15)' }}>
              <div style={{ fontFamily: "'Inter', sans-serif", fontSize: 12, color: '#6b6a5c' }}>
                {selectedMachines.length === 0 && 'No machines selected'}
                {selectedMachines.length > 0 && (
                  <>
                    <span style={{ color: '#d97706', fontWeight: 600 }}>{selectedMachines.length} machine(s)</span> selected for return
                    {selectedMachines.filter(s => s.wantsReplacement).length > 0 && (
                      <>, <span style={{ color: '#d97706', fontWeight: 600 }}>{selectedMachines.filter(s => s.wantsReplacement).length}</span> with replacement</>
                    )}
                  </>
                )}
              </div>

              <div style={{ display: 'flex', gap: 12 }}>
                <button type="button" onClick={onClose} style={ghostBtn}>Cancel</button>
                <button
                  type="submit"
                  disabled={selectedMachines.length === 0 || returnMutation.isPending}
                  style={{
                    ...primaryBtn,
                    opacity: (selectedMachines.length === 0 || returnMutation.isPending) ? 0.5 : 1,
                    cursor: (selectedMachines.length === 0 || returnMutation.isPending) ? 'not-allowed' : 'pointer'
                  }}
                >
                  {returnMutation.isPending ? 'Processing...' : 'Process Returns'}
                </button>
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};
